import React from 'react';
import { SimulationResult } from '../utils/financialEngine';
import { TrendingUp, DollarSign, Key, Home } from 'lucide-react';

interface KPICardsProps {
  simulation: SimulationResult;
}

const formatCurrency = (val: number) => {
  const rounded = Math.round(val);
  const prefix = rounded < 0 ? '-$' : '$';
  return `${prefix}${Math.abs(rounded).toLocaleString()}`;
};

export const KPICards: React.FC<KPICardsProps> = ({ simulation }) => {
  const { trajectory, saleSummary } = simulation;

  const y1 = trajectory.find((t) => t.year === 1) || trajectory[0];
  const y3 = trajectory.find((t) => t.year === 3) || trajectory[2];
  const y5 = trajectory.find((t) => t.year === 5) || trajectory[4];
  const y10 = trajectory.find((t) => t.year === 10) || trajectory[9];

  const keepLeads10Y = y10.netWorthDifference >= 0;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {/* Card 1: 10-Year Net Worth Delta */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-slate-900 to-slate-950 p-5 border border-slate-800/80 shadow-xl">
        <div className="absolute top-0 right-0 p-3 opacity-15">
          <TrendingUp className="w-20 h-20 text-cyan-400" />
        </div>
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-cyan-400 mb-1">
          <span>10-Year Trajectory Winner</span>
        </div>
        <div className="text-2xl font-extrabold font-heading text-white">
          {keepLeads10Y ? 'Keep & Rent' : 'Sell & Buy'}
        </div>
        <div className="mt-2 text-xs text-slate-400 flex items-center gap-1">
          Net Worth Delta:{' '}
          <span className={`font-mono font-bold text-sm ${keepLeads10Y ? 'text-emerald-400' : 'text-amber-400'}`}>
            {keepLeads10Y ? '+' : ''}{formatCurrency(y10.netWorthDifference)}
          </span>
        </div>
        <p className="text-[11px] text-slate-500 mt-1">
          {keepLeads10Y
            ? 'Retaining Home #1 accumulates higher total equity over 10 years.'
            : 'Selling Home #1 & downsizing debt yields a higher net worth balance.'}
        </p>
      </div>

      {/* Card 2: 3, 5, 10-Year Progression */}
      <div className="rounded-2xl bg-gradient-to-br from-slate-900 via-slate-900 to-slate-950 p-5 border border-slate-800/80 shadow-xl">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">
          <DollarSign className="w-4 h-4 text-emerald-400" />
          <span>Keep vs Sell Net Worth</span>
        </div>
        <div className="space-y-1.5 font-mono text-xs">
          <div className="flex justify-between items-center bg-slate-950/60 p-1.5 rounded border border-slate-800/50">
            <span className="text-slate-400 font-sans">Year 3:</span>
            <span className="text-white font-semibold">{formatCurrency(y3.keepRent.netWorth)} vs {formatCurrency(y3.sellBuy.netWorth)}</span>
          </div>
          <div className="flex justify-between items-center bg-slate-950/60 p-1.5 rounded border border-slate-800/50">
            <span className="text-slate-400 font-sans">Year 5:</span>
            <span className="text-white font-semibold">{formatCurrency(y5.keepRent.netWorth)} vs {formatCurrency(y5.sellBuy.netWorth)}</span>
          </div>
          <div className="flex justify-between items-center bg-slate-950/60 p-1.5 rounded border border-slate-800/50">
            <span className="text-slate-400 font-sans">Year 10:</span>
            <span className="text-cyan-400 font-bold">{formatCurrency(y10.keepRent.netWorth)} vs {formatCurrency(y10.sellBuy.netWorth)}</span>
          </div>
        </div>
      </div>

      {/* Card 3: Home #1 Net Monthly Cash Flow */}
      <div className="rounded-2xl bg-gradient-to-br from-slate-900 via-slate-900 to-slate-950 p-5 border border-slate-800/80 shadow-xl">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">
          <Key className="w-4 h-4 text-indigo-400" />
          <span>Home #1 Net Rental Flow</span>
        </div>
        <div className={`text-2xl font-extrabold font-mono ${y1.keepRent.home1MonthlyNetCashFlow >= 0 ? 'text-emerald-400' : 'text-amber-400'}`}>
          {formatCurrency(y1.keepRent.home1MonthlyNetCashFlow)}<span className="text-xs font-sans text-slate-400 font-normal">/mo</span>
        </div>
        <p className="text-[11px] text-slate-400 mt-2">
          Net of PITI, 5% Vacancy, 8% PM, 5% Maintenance & Depreciation Tax benefits.
        </p>
      </div>

      {/* Card 4: Sell & Buy Realized Proceeds */}
      <div className="rounded-2xl bg-gradient-to-br from-slate-900 via-slate-900 to-slate-950 p-5 border border-slate-800/80 shadow-xl">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">
          <Home className="w-4 h-4 text-cyan-400" />
          <span>Home #1 Net Sale Equity</span>
        </div>
        <div className="text-2xl font-extrabold font-mono text-white">
          {formatCurrency(saleSummary.netCashProceeds)}
        </div>
        <p className="text-[11px] text-slate-400 mt-2">
          After 6% closing fees and Sec. 121 capital gains tax exclusion ({saleSummary.exclusion > 0 ? `$${(saleSummary.exclusion / 1000).toFixed(0)}k` : 'None'}).
        </p>
      </div>
    </div>
  );
};
